import { FormProvider } from 'react-hook-form';
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@tinct/web-components';
import { createConnectedInputHelpers } from '@tinct/web-form';
import {
  ReadSsoConnection,
  SsoProvisioningFormValues,
} from '@tinct/core-api-models';
import { useSsoProvisioningForm } from '../hooks/use-sso-provisioning-form';
import { SECURITY_CARD_ANCHORS } from '../security-card-anchors';

const { ControlledSelectInput, ControlledSwitchInput } =
  createConnectedInputHelpers<SsoProvisioningFormValues>();

const ROLE_OPTIONS = [
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' },
];

const SESSION_LENGTH_OPTIONS = [
  { value: '28800', label: '8 hours' },
  { value: '86400', label: '24 hours' },
  { value: '259200', label: '3 days' },
  { value: '604800', label: '7 days' },
];

export type SsoProvisioningCardProps = {
  organizationId: string;
  connection: ReadSsoConnection;
};

/**
 * What a sign-in through the connection grants: the role people are created with, how long their
 * session lasts, and whether an unknown person is created at all.
 */
export function SsoProvisioningCard({
  organizationId,
  connection,
}: SsoProvisioningCardProps) {
  const { form, handleSubmit, isSaving } = useSsoProvisioningForm(
    organizationId,
    connection,
  );
  const rootError = form.formState.errors.root?.message;

  return (
    <Card id={SECURITY_CARD_ANCHORS.ssoProvisioning} tabIndex={-1}>
      <CardHeader>
        <CardTitle className="text-xl">Provisioning</CardTitle>
        <CardDescription>
          What people get when they sign in through your identity provider.
        </CardDescription>
      </CardHeader>

      <FormProvider {...form}>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-6 px-6">
            <ControlledSelectInput
              name="defaultRole"
              label="Role of new members"
              options={ROLE_OPTIONS}
              description="Admins are always appointed by hand: single sign-on never grants that role."
              direction="horizontal"
            />
            <ControlledSelectInput
              name="sessionMaxAgeSeconds"
              label="Session length"
              options={SESSION_LENGTH_OPTIONS}
              description="After this, people authenticate with your provider again — silently while their session there is still open."
              direction="horizontal"
            />
            <ControlledSwitchInput
              name="jitEnabled"
              label="Create accounts at first sign-in"
              description="Off, only people you invited — or that your directory provisions — can sign in through the connection; nobody else is created."
            />

            {rootError && (
              <p className="text-sm text-destructive">{rootError}</p>
            )}

            <div className="flex justify-end border-t pt-6">
              <Button
                type="submit"
                disabled={!form.formState.isDirty}
                isLoading={isSaving}
              >
                Save
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </Card>
  );
}
